import { Box, InputBase, Paper, Theme, ToggleButton, toggleButtonClasses, ToggleButtonGroup, toggleButtonGroupClasses, Typography, useMediaQuery } from '@mui/material';
import AppLayout from '../Components/AppLayout';
import { makeStyles, styled } from '@mui/styles';
import { useMemo, useState, useEffect, useContext } from 'react';
import { AxiosResponse } from 'axios';
import { getMeetings } from '../DashBoard/Api';
import Loaders from '../../../GlobalComponents/KenLoader';
import StartupTable from '../Components/StartupTable';
import { useQuery } from '@tanstack/react-query';
import storageService from '../../../Utils/storageService';
import { AuthContext } from '../../../Helpers/Contexts/authContext';

const useStyles = makeStyles(() => ({
  title: {
    fontSize: '14px !important',
    fontWeight: '500 !important',
    '@media (min-width: 1920px)': {
      fontSize: '18px !important',
    },
  },
  cardBgs: {
    borderRadius: '8px',
    width: '83%',
    height: '84vh',
    border: '1px solid #E6E6E6',
    background: '#fff',
    padding: '20px',
    overflow: 'auto',
    '@media (max-width: 600px)': {
      width: '100%',
      height: 'auto',
      background: 'transparent',
      border: 'none',
      padding: '0px',
    },
  },
  searchPaper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid #E6E6E6',
    boxShadow: 'none',
    background: '#FFFFFF',
    width: '340px',
    '@media (max-width: 600px)': {
      width: '100%',
    },
  },
  searchInput: {
    flex: 1,
    fontSize: '14px',
    fontFamily: '"General Sans", sans-serif',
    color: '#333333',
    '& input::placeholder': {
      color: '#9AA0A6',
      opacity: 1,
    },
  },
}));

const AppToggleButton = styled(ToggleButtonGroup)(({ theme }: { theme: Theme }) => ({
  border: '1px solid #E6E6E6',
  borderRadius: '12px',
  padding: '4px',
  background: '#FFFFFF',
  [`& .${toggleButtonGroupClasses.grouped}, & .${toggleButtonGroupClasses.groupedHorizontal}`]: {
    margin: theme.spacing(0),
    border: '0 !important',
    borderLeft: '0 !important',
    '&:not(:first-of-type)': {
      borderLeft: '0 !important',
    },
  },
  [`& .${toggleButtonClasses.root}`]: {
    border: '0 !important',
    borderRadius: '10px !important',
    textTransform: 'none',
    fontFamily: '"General Sans", sans-serif',
    fontWeight: 600,
    color: '#585A64',
    background: 'transparent',
    '&.Mui-selected': {
      background: '#E9EAEE',
      color: '#1F1F1F',
      '&:hover': {
        background: '#E0E2E8',
        color: '#1F1F1F',
      },
    },
    '&:hover': {
      background: '#F4F5F7',
    },
  },
}));

const buttonsArray = ['Current Evaluation', 'Completed Evaluation'];
const buttonsArrayII = ['Current', 'Completed'];

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
      stroke="#585A64"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.5013 17.4974L14.168 14.1641"
      stroke="#585A64"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AssignedStartUps: React.FunctionComponent = () => {
  const classes = useStyles();
  const [contactId, setContactId] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const isMaxScreen = useMediaQuery('(min-width:1920px)');
  const { state: { user } } = useContext(AuthContext);

  const [toggleValue, setToggleValue] = useState<string>(isMobile ? 'Current' : 'Current Evaluation');
  const [searchValue, setSearchValue] = useState<string>('');
  const [startupData, setStartupData] = useState<any>({
    Current: [],
    Completed: [],
    'Current Evaluation': [],
    'Completed Evaluation': [],
  });

  // Load contactId from storage on component mount
  useEffect(() => {
    const loadStorageData = async () => {
      try {
        const contactIdData = await storageService.getItem('contactId');
        setContactId(contactIdData);
      } catch (error) {
        console.error('Error loading storage data:', error);
      }
    };
    loadStorageData();
  }, []);

  const handleChange = (value: string) => {
    setToggleValue(value);
  };

  const filteredStartups = useMemo(() => {
    const rawData = startupData[toggleValue] || [];
    const q = searchValue.trim().toLowerCase();
    if (!q) return rawData;

    return rawData.filter((row: any) => {
      const startupName =
        row?.Application__r?.Name_of_Product_or_Startup_Venture__c ||
        row?.Application__r?.Name ||
        '';
      const industry = row?.Application__r?.Industry__c || '';
      const createdDateText = row?.CreatedDate ? new Date(row.CreatedDate).toLocaleDateString() : '';

      const haystack = `${startupName} ${industry} ${createdDateText}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [searchValue, startupData, toggleValue]);

  const getMeetingData = useQuery({
    queryKey: ['getMeeting', user?.accountId],
    queryFn: async () => {
      const response: AxiosResponse = await getMeetings(user?.accountId || '');

      const currentDate = new Date();
      const assignments = response?.data?.data?.assignments;
      const meetings = response?.data?.data?.meetings;
      const upcomingMeetings = meetings.filter((meeting: any) => {
        const startDate: any = new Date(meeting.End_Date_Time__c);
        return startDate >= currentDate;
      });

      if (assignments) {
        const customData: any = {
          Current: [],
          Completed: [],
          'Current Evaluation': [],
          'Completed Evaluation': [],
        };
        
        for (let i = 0; i < assignments.length; i++) {
          const element = assignments[i];
          const getMeeting = upcomingMeetings.find((items: any) => {
            return items?.Application__c === element?.Application__c;
          });

          if (element?.Evaluation_Scores__r?.records[0]?.Evaluated__c === true) {
            customData['Completed'].push({ ...element, ...getMeeting });
            customData['Completed Evaluation'].push({ ...element, ...getMeeting });
          } else {
            customData['Current'].push({ ...element, ...getMeeting });
            customData['Current Evaluation'].push({ ...element, ...getMeeting });
          }
        }

        setStartupData(customData);
      }

      return response;
    },
    refetchOnWindowFocus: false,
    enabled: !!user?.accountId,
  });

  return (
    <AppLayout>
      {getMeetingData.isLoading && <Loaders />}
      <Box
        component={'div'}
        className={`${classes.cardBgs} d-flex flex-column`}
        gap={isMobile ? '10px' : '20px'}
      >
        <Typography className={classes.title} color={'#333333'}>
          Assigned Startups
        </Typography>
        <Box
          component={'div'}
          mt={2}
          display="flex"
          alignItems={isMobile ? 'stretch' : 'center'}
          justifyContent="space-between"
          gap={isMobile ? '12px' : '16px'}
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <AppToggleButton
            onChange={(event, value) => {
              if (value !== null) {
                handleChange(value);
                setSearchValue('');
              }
            }}
            sx={{
              width: isMobile ? '100%' : 'auto',
              justifyContent: isMobile ? 'space-between' : 'flex-start',
            }}
            value={toggleValue}
            exclusive
            size="small"
          >
            {(isMobile ? buttonsArrayII : buttonsArray).map((items, index) => {
              return (
                <ToggleButton
                  key={index}
                  size="small"
                  sx={{
                    padding: isMobile ? '6px 10px' : '10px 14px',
                    fontSize: isMobile ? '12px' : isMaxScreen ? '18px' : '14px',
                    textTransform: 'none',
                    flex: isMobile ? 1 : 'unset',
                  }}
                  value={items}
                >
                  {items}
                </ToggleButton>
              );
            })}
          </AppToggleButton>

          <Paper className={classes.searchPaper}>
            <SearchIcon />
            <InputBase
              className={classes.searchInput}
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              inputProps={{ 'aria-label': 'search assigned startups' }}
            />
          </Paper>
        </Box>

        <Box mt={2} width={'100%'}>
          <StartupTable 
            key={`${toggleValue}-${searchValue}`}
            data={filteredStartups}
            showPagination={true}
            rowsPerPageOptions={[5, 10, 25]}
            defaultRowsPerPage={10}
          />
        </Box>
      </Box>
    </AppLayout>
  );
};

export default AssignedStartUps;
